import { Ionicons } from '@expo/vector-icons';
import { router } from 'expo-router';
import React, { useEffect, useState } from 'react';
import { View } from 'react-native';

import { ThemedText } from '@/components/themed-text';
import { FadeInView } from '@/components/ui/fade-in-view';
import { PressableScale } from '@/components/ui/pressable-scale';
import { ENTRY_CHARGES_BANNER } from '@/content/payments';
import { useCanManagePaymentAccount } from '@/hooks/use-membership';
import { usePaymentAccount } from '@/hooks/use-payment-account';
import {
  dismissChargesBanner,
  loadChargesBannerDismissedUntil,
} from '@/lib/payments-charges-banner';

export interface ChargesAutomaticBannerProps {
  /**
   * Pagamentos confirmados na mão este mês, contados de quem chama (tela de
   * Cobranças, T4.5) — **só da página já carregada da lista** (a API não dá
   * uma contagem própria do mês inteiro). Quando o filtro/período mudam para
   * algo diferente de "mês atual, todas", quem chama passa `0` para esconder
   * a faixa em vez de mostrar um número que não bate com o mês.
   */
  manualConfirmedThisMonthCount: number;
}

const THRESHOLD = 5;

/**
 * Faixa em Cobranças, depois do 5º pagamento confirmado na mão no mês
 * (PLANO-RECEBIMENTO-AUTOMATICO.md, seção 6.1, T4.5): discreta, fechável,
 * lembra por 30 dias (`lib/payments-charges-banner.ts`). Some para quem já
 * ativou o recebimento automático (qualquer status) e para quem não gerencia
 * a subconta — `useCanManagePaymentAccount` (T7.2), mesma regra do resto do
 * app.
 */
export function ChargesAutomaticBanner({
  manualConfirmedThisMonthCount,
}: ChargesAutomaticBannerProps) {
  const canManage = useCanManagePaymentAccount();
  const { data: statusData, isLoading: isAccountLoading } = usePaymentAccount();
  // `null` = ainda não sabemos (evita piscar a faixa antes de checar o storage).
  const [snoozed, setSnoozed] = useState<boolean | null>(null);

  useEffect(() => {
    let mounted = true;
    void loadChargesBannerDismissedUntil().then((until) => {
      if (mounted) setSnoozed(!!until && until.getTime() > Date.now());
    });
    return () => {
      mounted = false;
    };
  }, []);

  const eligible =
    canManage &&
    !isAccountLoading &&
    !statusData?.account &&
    manualConfirmedThisMonthCount >= THRESHOLD;

  if (!eligible || snoozed !== false) return null;

  const handleDismiss = () => {
    setSnoozed(true);
    void dismissChargesBanner();
  };

  return (
    <FadeInView>
      <View
        className="flex-row items-center gap-3 rounded-2xl border border-neutral-900/5 bg-card p-4"
        style={{ borderCurve: 'continuous' }}
      >
        <View className="h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 dark:bg-primary">
          <Ionicons className="text-primary dark:text-white" name="flash-outline" size={18} />
        </View>
        <View className="min-w-0 flex-1 gap-1">
          <ThemedText className="text-sm leading-5 text-foreground" numberOfLines={2}>
            {ENTRY_CHARGES_BANNER.title(manualConfirmedThisMonthCount)}
          </ThemedText>
          <PressableScale
            haptic
            accessibilityRole="button"
            onPress={() => router.push('/(tabs)/(provider)/payments/about' as never)}
            testID="charges-automatic-banner-cta"
          >
            <ThemedText className="font-semibold text-xs text-primary">
              {ENTRY_CHARGES_BANNER.ctaLabel}
            </ThemedText>
          </PressableScale>
        </View>
        <PressableScale
          haptic
          accessibilityRole="button"
          accessibilityLabel="Fechar aviso"
          onPress={handleDismiss}
          className="h-8 w-8 shrink-0 items-center justify-center rounded-full"
          testID="charges-automatic-banner-dismiss"
        >
          <Ionicons className="text-neutral-400" name="close" size={16} />
        </PressableScale>
      </View>
    </FadeInView>
  );
}
